/**
 * useDemoSession - Hook for managing demo mode sessions.
 *
 * NEW ARCHITECTURE (no Supabase Auth):
 * - User enters a 6-character demo code → validate_demo_code_anon RPC validates and
 *   returns BOTH a demo audit token (30 days) AND a long-lived player_session token
 *   for the default demo student (Aino).
 * - The player_session token is persisted via SessionTokenManager so the regular
 *   StudentDashboard works exactly as it would for a real student.
 * - No Supabase Auth signin happens. The demo "teacher" view uses a separate
 *   anonymous RPC (get_demo_group_overview) and does NOT require a Supabase session.
 * - All writes are blocked client-side (see supabase/client.ts demo proxy).
 */
import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import SessionTokenManager from '@/services/sessionTokenManager';
import { SessionManager } from '@/services/sessionManager';

const STORAGE_KEY = 'ta_demo_session_v2';

export type DemoView = 'student' | 'teacher' | null;

export interface DemoSessionState {
  demoToken: string;
  expiresAt: string; // ISO
  view: DemoView;
  // Original 6-char demo code (uppercase) — used to detect when a QR with a
  // different code is opened on top of an existing demo session.
  code?: string;
  // Demo player session info (real player_session token for the default demo student)
  playerSessionToken: string;
  playerId: string;
  playerName: string;
  groupId: string;
  groupName: string;
}

interface UseDemoSessionResult {
  isDemo: boolean;
  view: DemoView;
  expiresAt: Date | null;
  demoToken: string | null;
  activeCode: string | null;
  setView: (view: DemoView) => void;
  clear: () => Promise<void>;
  validate: (code: string) => Promise<{ ok: boolean; error?: string }>;
  /** Open a public demo without requiring a 6-char code. */
  startPublic: () => Promise<{ ok: boolean; error?: string }>;
}

function readStorage(): DemoSessionState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DemoSessionState;
    if (!parsed.demoToken || !parsed.expiresAt || !parsed.playerSessionToken) return null;
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStorage(state: DemoSessionState | null) {
  if (state === null) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }
  window.dispatchEvent(new Event('demo-session-changed'));
}

export function useDemoSession(): UseDemoSessionResult {
  const [state, setState] = useState<DemoSessionState | null>(() => readStorage());

  useEffect(() => {
    const sync = () => setState(readStorage());
    window.addEventListener('demo-session-changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('demo-session-changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const setView = useCallback((view: DemoView) => {
    const current = readStorage();
    if (!current) return;
    writeStorage({ ...current, view });
  }, []);

  const clear = useCallback(async () => {
    // Clear player session token (SessionTokenManager handles localStorage)
    try { SessionTokenManager.reset(); } catch { /* ignore */ }
    try { SessionManager.clearSession(); } catch { /* ignore */ }
    writeStorage(null);
  }, []);

  const validate = useCallback(async (code: string): Promise<{ ok: boolean; error?: string }> => {
    try {
      const cleaned = code.trim().toUpperCase();
      if (cleaned.length !== 6) {
        return { ok: false, error: 'Koodin tulee olla 6 merkkiä pitkä.' };
      }
      const { data, error } = await supabase.rpc('validate_demo_code_anon', { p_code: cleaned });
      if (error) {
        return { ok: false, error: 'Virheellinen koodi.' };
      }
      const row: any = Array.isArray(data) ? data[0] : data;
      if (!row?.demo_token || !row?.player_session_token) {
        return { ok: false, error: 'Virheellinen koodi.' };
      }

      // Persist the demo player_session token so the StudentDashboard works
      // exactly like for a real student. SessionTokenManager handles bootstrap.
      const expiresIso = new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString();
      SessionTokenManager.persistToken(row.player_session_token, row.player_id, expiresIso);
      // CRITICAL: Actually set the token in the DB session config so RLS works.
      // persistToken alone does NOT do this — without it, the StudentDashboard
      // queries fail and DemoProtectedRoute bounces back to /demo causing a
      // visible "vibration" loop on first entry.
      try {
        await SessionTokenManager.ensureTokenSet(row.player_session_token);
      } catch { /* ignore — bootstrap will retry */ }
      SessionManager.setSession({
        sessionToken: row.player_session_token,
        playerId: row.player_id,
        playerName: row.player_name,
        groupId: row.group_id,
        expiresAt: expiresIso,
      });

      writeStorage({
        demoToken: row.demo_token,
        expiresAt: row.expires_at,
        view: null,
        code: cleaned,
        playerSessionToken: row.player_session_token,
        playerId: row.player_id,
        playerName: row.player_name,
        groupId: row.group_id,
        groupName: row.group_name ?? '9A Demo',
      });
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err?.message ?? 'Tuntematon virhe.' };
    }
  }, []);

  const startPublic = useCallback(async (): Promise<{ ok: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.rpc('start_public_demo' as any);
      if (error) {
        return { ok: false, error: 'Demoa ei voitu avata. Yritä uudelleen.' };
      }
      const row: any = Array.isArray(data) ? data[0] : data;
      if (!row?.demo_token || !row?.player_session_token) {
        return { ok: false, error: 'Demoa ei voitu avata.' };
      }

      const expiresIso = new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString();
      SessionTokenManager.persistToken(row.player_session_token, row.player_id, expiresIso);
      try {
        await SessionTokenManager.ensureTokenSet(row.player_session_token);
      } catch { /* ignore */ }
      SessionManager.setSession({
        sessionToken: row.player_session_token,
        playerId: row.player_id,
        playerName: row.player_name,
        groupId: row.group_id,
        expiresAt: expiresIso,
      });

      writeStorage({
        demoToken: row.demo_token,
        expiresAt: row.expires_at,
        view: null,
        code: 'PUBLIC',
        playerSessionToken: row.player_session_token,
        playerId: row.player_id,
        playerName: row.player_name,
        groupId: row.group_id,
        groupName: row.group_name ?? '9A Demo',
      });
      return { ok: true };
    } catch (err: any) {
      return { ok: false, error: err?.message ?? 'Tuntematon virhe.' };
    }
  }, []);

  return {
    isDemo: !!state,
    view: state?.view ?? null,
    expiresAt: state ? new Date(state.expiresAt) : null,
    demoToken: state?.demoToken ?? null,
    activeCode: state?.code ?? null,
    setView,
    clear,
    validate,
    startPublic,
  };
}

/**
 * Lightweight read-only check (no React hook needed).
 * Returns true if a non-expired demo session is in storage.
 */
export function isDemoModeActive(): boolean {
  return !!readStorage();
}

/**
 * Returns the active demo audit token (or null). Used by the demo-teacher
 * dashboard to call get_demo_group_overview.
 */
export function getDemoToken(): string | null {
  return readStorage()?.demoToken ?? null;
}
